/**
 * spaceBomberOrdnanceBuilders.ts
 * Camera-relative rect builders for the BomberDrone's two-tier
 * ordnance stream: cluster bombs (parent payload) and bomb shrapnel
 * (post-detonation fragments).
 *
 * Extracted from `spaceDrawBuilders.ts` so that file stays under the
 * AGENTS.md 300-line cap. Sibling to `spaceDrawBuilders` —
 * `buildSpaceDrawList` calls both helper modules during draw-list
 * assembly. No game-state mutation — both builders are read-only over
 * `spaceState`.
 *
 * @module spaceBomberOrdnanceBuilders
 */

import { spaceState } from '../engine/spaceState';
import {
  SPACE_BOMBER_BOMB_COLOR,
  SPACE_BOMBER_SHRAPNEL_COLOR,
} from '../../constants/rendering';
import type { SpaceRect } from './spaceRenderTypes';

/**
 * Build camera-relative rectangles for every live bomber cluster bomb.
 *
 * @param cameraX - Current camera left-edge X.
 */
export function buildClusterBombRects(cameraX: number): SpaceRect[] {
  const out: SpaceRect[] = [];
  for (const b of spaceState.clusterBombs) {
    if (b.dead) continue;
    out.push({
      x: b.x - cameraX,
      y: b.y,
      width: b.width,
      height: b.height,
      color: SPACE_BOMBER_BOMB_COLOR,
      // DECISION: `cb` prefix keeps bomb keys distinct from every
      // other bolt / missile stream.
      key: `cb${b.spawnId}`,
    });
  }
  return out;
}

/**
 * Build camera-relative rectangles for every live shrapnel fragment.
 * Sibling to `buildClusterBombRects` — same shape, different source
 * array and palette.
 *
 * @param cameraX - Current camera left-edge X.
 */
export function buildBombShrapnelRects(cameraX: number): SpaceRect[] {
  const out: SpaceRect[] = [];
  for (const s of spaceState.bombShrapnel) {
    if (s.dead) continue;
    out.push({
      x: s.x - cameraX,
      y: s.y,
      width: s.width,
      height: s.height,
      color: SPACE_BOMBER_SHRAPNEL_COLOR,
      // DECISION: `bsh` (not `bs`) — `buildBossShieldRects` already
      // emits `bs${i}` for boss shield nodes. A shared `bs` prefix
      // would collide whenever `spawnId === nodeIndex` (any small
      // spawn count), so the shrapnel stream uses a distinct prefix.
      key: `bsh${s.spawnId}`,
    });
  }
  return out;
}
